<template>
  <!-- Learning Insights Panel -->
  <aside :class="[isCollapsed ? 'w-0 opacity-0' : 'w-1/4']"
    class="relative h-[calc(100vh-4rem)] bg-gray-50 p-4 border-l z-40 overflow-y-auto">
    <!-- Apply opacity only to the sidebar when loading -->
    <div v-if="explanationStore.loading" class="absolute inset-0 bg-gray-100 opacity-50"></div>
    <div class="flex justify-between items-center mb-4">
      <h2 class="text-xl font-bold">{{ $t('insightsSidebar.title') }}</h2>
      <button @click="closeSidebar" class="text-gray-500 text-2xl">
        {{ $t('insightsSidebar.closeButton') }}
      </button>
    </div>
    <!-- Selected message -->
    <div
      class="mb-4 p-2 bg-gray-50 italic border-l-4 border-gray-500">
      {{ explanationStore.selectedMessage }}
    </div>

    <!-- Loading State -->
    <div v-if="explanationStore.loading" class="absolute inset-0 flex justify-center items-center z-20">
      <svg class="animate-spin h-10 w-10 text-gray-500" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
      </svg>
    </div>

    <!-- Error State -->
    <div v-if="explanationStore.hasError" class="flex flex-col items-center justify-center p-4 text-center">
      <img src="@/assets/PapoteAI_Error.png" alt="Error" class="w-24 h-24 mb-4">
      <h3 class="text-xl font-bold text-red-600 mb-2">{{ $t('insightsSidebar.errorTitle') }}</h3>
      <p class="text-gray-700">
        {{ $t(explanationStore.errorType === 'credits' ? 'insightsSidebar.creditsError' : 'insightsSidebar.generalError') }}
      </p>
    </div>

    <!-- Insights panel contents -->
    <div v-else-if="canShowInsights" class="mt-4">
      <div v-if="explanationStore.grammar" class="mb-4 p-3 rounded-md bg-paletteYellow bg-opacity-20 border border-paletteYellow">
        <h3 class="font-bold text-yellow-700">{{ $t('insightsSidebar.grammarNoteTitle') }}</h3>
        <p class="text-sm text-yellow-900">
          {{ explanationStore.grammar.feedback }}
        </p>
        <template v-if="explanationStore.grammar?.suggestion">
          <h4 class="mt-2 italic text-yellow-700">{{ $t('insightsSidebar.suggestion') }}</h4>
          <p class="p-2 italic border-l-4 border-yellow-700 text-yellow-700">
            {{ explanationStore.grammar.suggestion }}
          </p>
        </template>
      </div>

      <div v-if="explanationStore.vocabulary" class="mb-4 p-3 rounded-md bg-paletteLightGreen bg-opacity-20 border border-paletteLightGreen">
        <h3 class="font-bold text-green-700">{{ $t('insightsSidebar.vocabularyTitle') }}</h3>
        <p class="text-sm text-green-900">
          {{ explanationStore.vocabulary.feedback }}
        </p>
        <template v-if="explanationStore.vocabulary?.suggestion">
          <h4 class="mt-2 italic text-green-900">{{ $t('insightsSidebar.suggestion') }}</h4>
          <p class="p-2 italic border-l-4 border-green-900 text-green-900">
            {{ explanationStore.vocabulary.suggestion }}
          </p>
        </template>
      </div>

      <div v-if="explanationStore.cultural" class="mb-4 p-3 rounded-md bg-paletteOrange bg-opacity-20 border border-paletteOrange">
        <h3 class="font-bold text-orange-700">{{ $t('insightsSidebar.culturalNoteTitle') }}</h3>
        <p class="text-sm text-orange-900">
          {{ explanationStore.cultural.feedback }}
        </p>
        <template v-if="explanationStore.cultural?.suggestion">
          <h4 class="mt-2 italic text-orange-700">{{ $t('insightsSidebar.suggestion') }}</h4>
          <p class="p-2 italic border-l-4 border-orange-700 text-orange-700">
            {{ explanationStore.cultural.suggestion }}
          </p>
        </template>
      </div>

      <div v-if="!explanationStore.selectedMessageIsUser && explanationStore.translation"
           class="p-3 rounded-md bg-paletteDarkGreen bg-opacity-20 border border-paletteDarkGreen">
        <h3 class="font-bold text-green-900">{{ $t('insightsSidebar.translationTitle') }}</h3>
        <p class="text-sm text-green-1100">
          {{ explanationStore.translation }}
        </p>
      </div>
    </div>
  </aside>
</template>

<script setup>
import { computed } from 'vue'
import { useExplanationStore } from '@/stores/explanations'

const explanationStore = useExplanationStore()
const emit = defineEmits(['close-sidebar'])
const isCollapsed = computed(() => explanationStore.selectedMessage === null)
const canShowInsights = computed(() => (explanationStore.selectedMessage !== null) && !explanationStore.loading && !explanationStore.hasError)

function closeSidebar () {
  explanationStore.clearSelectedMessage()
  emit('close-sidebar')
}
</script>
